import { defineRule } from 'vee-validate'
import { confirmed, alpha_dash } from '@vee-validate/rules'
import _debounce from 'lodash/debounce'
import { fbApi } from '@/utils/http-api'
import { commonPasswords } from '@/utils/forms/common-passwords'

/**
 * Do not allow fb-* usernames
 */
export function fbUsernameRule(value: any): boolean | string {
  if (value.startsWith('fb-')) {
    return 'This is a reserved username'
  }

  return true
}

/**
 * Verify username is unique.
 */
export async function fbUsernameUniqueRule(value: any): Promise<boolean | string> {
  try {
    // skip if value is empty
    if (!value) {
      return false
    }

    const res = await fbApi.post(`/validate/unique-username`, {
      skipErrorModal: true,
      json: {
        username: value,
      },
    })

    // invalid
    if (res.body.status !== 'success') {
      return res.body.message
    }

    // valid
    return true
  } catch (err) {
    // unknown error
    console.error(err)
    return 'Unable to verify username, please refresh the page and try again'
  }
}

/**
 * Verify email is unique.
 */
export async function fbEmailUniqueRule(value: any, [force]: string[]): Promise<boolean | string> {
  try {
    // skip if value is empty
    if (!value) {
      return false
    }

    const res = await fbApi.post(`/validate/unique-email`, {
      skipErrorModal: true,
      json: {
        email: value,
        // tells API to force check against is_blender emails
        force: force === 'force' ? 1 : 0,
      },
    })

    // invalid
    if (res.body.status !== 'success') {
      return res.body.message
    }

    // valid
    return true
  } catch (err) {
    // unknown error
    console.error(err)
    return 'Unable to verify email, please refresh the page and try again'
  }
}

/**
 * Make sure value is not equal to another form field value.
 * EX: password should not equal username
 */
export function fbNotSameAsRule(value: any, [otherFieldName]: string[]): boolean | string {
  const otherField = document.querySelector(`input[name="${otherFieldName}"]`)

  if (otherField instanceof HTMLInputElement && value === otherField.value) {
    return `Cannot be the same as ${otherFieldName}`
  }

  return true
}

/**
 * Password strength rules
 */
export function fbPasswordStrengthRule(value: any): boolean | string {
  // not doing required check
  if (!value) {
    return true
  }

  // must have upper / lower / digit
  if (!/[a-z]/.test(value) || !/[A-Z]/.test(value) || !/[0-9]/.test(value)) {
    return 'Must contain at least one lowercase, uppercase, and number'
  }

  // I have no idea where commonPasswords came from, before my time... but make
  // sure input value doesn't match any from the list.
  if (commonPasswords.includes(value)) {
    return 'This password is too easy'
  }

  return true
}

/**
 * Helper to convert from server error bag to vee valdiate initialErrors.
 * Server returns array of errors per key, but vee-validate expects just a single string.
 */
export function errorBagToInitialErrors(
  errorBag: Record<string, string | string[]> | null,
): Record<string, string> {
  if (!errorBag) {
    return {}
  }

  // { firstName: 'some error' ... }
  return Object.keys(errorBag).reduce(
    (acc, key) => {
      if (Array.isArray(errorBag[key]) && errorBag[key].length) {
        acc[key] = errorBag[key][0]
        return acc
      }

      if (typeof errorBag[key] === 'string') {
        acc[key] = errorBag[key] as string
      }

      return acc
    },
    {} as Record<string, string>,
  )
}

/**
 * Primary export, intended to be called from components that need them,
 * not globally in app.ts.
 */
export function initExtraValidationRules() {
  // additional rules lazy loaded
  // IMPORTANT - don't forget to put validation messages in initVeeValidate
  defineRule('confirmed', confirmed)
  defineRule('alpha_dash', alpha_dash)

  // custom rules
  defineRule('fbUsername', fbUsernameRule)
  defineRule('fbNotSameAs', fbNotSameAsRule)
  defineRule('fbPasswordStrengthRule', fbPasswordStrengthRule)

  // async rules, debounce so vee-validate doesn't call crazy amount of times
  // in local dev with hot reloading
  defineRule(
    'fbUsernameUnique',
    _debounce(fbUsernameUniqueRule, 50, { leading: true, trailing: false }),
  )
  defineRule('fbEmailUnique', _debounce(fbEmailUniqueRule, 50, { leading: true, trailing: false }))
}
